import PropTypes from 'prop-types';
import Link from '@activebrands/core-web/components/Link';
import { TrackingLists } from '@activebrands/core-web/libs/Tracking/constants';
import useReInitWidgets from '@activebrands/core-web/libs/lipscore/hooks/useReInitWidgets';
import { useStyletron } from '@activebrands/core-web/libs/styletron';
import getSelector from '@activebrands/core-web/utils/get-selector';
import { useLocation } from '@reach/router';
import ArticleCard from 'components/ArticleCard';
import FeatureCard from 'components/FeatureCard';
import ArrowButton from 'components/buttons/ArrowButton';
import ProductCard from 'components/product/ProductCard';
import PromotionalSlider from 'components/slider/PromotionalSlider';

const PromotionSlider = ({
    $style = {},
    button = {},
    heading,
    headingFontKeys,
    id,
    imageSettings,
    items = [],
    optiId,
    slidesPerView,
    tag,
    paragraph,
    sbEditable,
    selector,
    type,
}) => {
    const [css] = useStyletron();
    const location = useLocation();
    const headingLabel = heading?.text?.trim();
    const buttonAriaLabel =
        type !== 'feature' && headingLabel && button.label ? `${button.label}: ${headingLabel}` : undefined;
    useReInitWidgets(location.href);

    const displaySlider = items.length > 2;

    return (
        <div className={css($style)} data-selector={selector} data-opti-id={optiId} {...sbEditable}>
            <PromotionalSlider
                isDraggable
                selector={selector}
                $paginationStyle={{
                    display: displaySlider ? ['none', null, null, null, 'initial'] : 'none',
                }}
                button={button}
                heading={heading}
                headingFontKeys={headingFontKeys}
                id={id}
                slidesPerView={slidesPerView}
                snapAlign="none"
                spaceBetween="4px"
                tag={tag}
                paragraph={paragraph}
                type={type}
            >
                {items.map((item, i) => {
                    if (type === 'article') {
                        return (
                            <ArticleCard
                                {...item}
                                imageSettings={imageSettings}
                                key={item.id}
                                loading="lazy"
                                position={i}
                                trackingList={TrackingLists.PRODUCT_GALLERY}
                            />
                        );
                    }

                    if (type === 'feature') {
                        return <FeatureCard {...item} imageSettings={imageSettings} key={item.id} loading="lazy" />;
                    }

                    if (type === 'product') {
                        return (
                            <ProductCard
                                {...item}
                                media={item.media?.length > 0 ? item.media.slice(0, 1) : []}
                                key={item.id}
                                imageSettings={imageSettings}
                                loading="lazy"
                                position={i}
                                trackingList={TrackingLists.PRODUCT_GALLERY}
                            />
                        );
                    }

                    return null;
                })}
            </PromotionalSlider>
            {button.label && button.url && (
                <ArrowButton
                    $style={
                        type === 'product'
                            ? { marginTop: '24px', width: ['100%', null, 'auto'], display: ['flex', null, 'none'] }
                            : {
                                marginTop: '24px',
                                width: ['100%', null, null, null, null, 'auto'],
                                display: ['flex', null, null, null, null, 'none'],
                            }
                    }
                    as={Link}
                    aria-label={buttonAriaLabel}
                    rel={button.rel}
                    theme={button.theme}
                    to={button.url}
                    data-selector={getSelector(selector, 'cta')}
                    {...button.sbEditable}
                >
                    {button.label}
                </ArrowButton>
            )}
        </div>
    );
};

PromotionSlider.propTypes = {
    $style: PropTypes.object,
    button: PropTypes.object,
    heading: PropTypes.object,
    headingFontKeys: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    id: PropTypes.string,
    imageSettings: PropTypes.shape({
        width: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)]),
        sizes: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
    }).isRequired,
    items: PropTypes.array,
    optiId: PropTypes.string,
    paragraph: PropTypes.object,
    sbEditable: PropTypes.object,
    selector: PropTypes.string,
    slidesPerView: PropTypes.oneOfType([PropTypes.number, PropTypes.arrayOf(PropTypes.number)]),
    tag: PropTypes.object,
    type: PropTypes.oneOf(['product', 'article', 'feature']),
};

export default PromotionSlider;
